import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { handleApiError } from '@/lib/api/axios'
import { useCan } from '@/hooks/useCan'
import { mfaPolicyApi, mfaPolicyKey } from '../api'

/**
 * Settings: "every user needs a second factor" (MFA build plan, Stage 3).
 *
 * The switch comes with the list of who it would stop: every active account with no sign-in
 * device. From the moment it is on, a right password earns each of them only the enrolment-code
 * screen, so each needs a code first - the list says who already has one waiting. Switching
 * needs a reason and security.mfa.manage; without the permission the card is not shown at all.
 */
export function MfaPolicyCard() {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const mayManage = useCan('security.mfa.manage')
    const [reason, setReason] = useState('')
    const [stepUpReason, setStepUpReason] = useState('')
    const policy = useQuery({ queryKey: mfaPolicyKey, queryFn: mfaPolicyApi.get, enabled: mayManage })
    const change = useMutation({
        mutationFn: (mandatory: boolean) => mfaPolicyApi.set(mandatory, reason.trim()),
        onSuccess: (data) => { setReason(''); queryClient.setQueryData(mfaPolicyKey, data); queryClient.invalidateQueries({ queryKey: mfaPolicyKey }) },
    })
    const changeStepUp = useMutation({
        mutationFn: (enforced: boolean) => mfaPolicyApi.setStepUp(enforced, stepUpReason.trim()),
        onSuccess: () => { setStepUpReason(''); queryClient.invalidateQueries({ queryKey: mfaPolicyKey }) },
    })
    if (!mayManage) return null
    const data = policy.data
    const unready = data?.withoutDevice ?? []
    const withoutCode = unready.filter((u) => u.code !== 'active').length

    return (
        <Card data-testid="mfa-policy-card">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5" />
                    {t('mfaPolicy.title')}
                </CardTitle>
                <CardDescription>{t('mfaPolicy.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {policy.isLoading && <p className="text-sm text-muted-foreground">{t('devices.loading')}</p>}
                {policy.isError && <p className="text-sm text-destructive" role="alert">{handleApiError(policy.error)}</p>}
                {data && (
                    <>
                        <p className="text-sm font-medium" data-testid="mfa-policy-state" data-mandatory={data.mandatory}>
                            {data.mandatory ? t('mfaPolicy.stateOn') : t('mfaPolicy.stateOff')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            {t('mfaPolicy.counts', { withDevice: data.withDevice, total: data.activeUsers })}
                        </p>
                        {unready.length > 0 && (
                            <div className="rounded-md border border-destructive/40 p-3" data-testid="mfa-policy-unready">
                                <p className="text-sm font-medium">
                                    {data.mandatory ? t('mfaPolicy.unreadyOn', { count: data.withoutDeviceCount }) : t('mfaPolicy.unreadyOff', { count: data.withoutDeviceCount })}
                                </p>
                                <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto text-sm">
                                    {unready.map((u) => (
                                        <li key={u.id} className="flex items-center justify-between gap-2" data-testid="unready-user" data-code={u.code ?? 'none'}>
                                            <span className="min-w-0 truncate">{u.name || u.email} <span className="text-muted-foreground">· {u.email}</span></span>
                                            <span className={u.code === 'active' ? 'shrink-0 text-xs text-muted-foreground' : 'shrink-0 text-xs text-destructive'}>
                                                {t(u.code === 'active' ? 'mfaPolicy.codeReady' : u.code === 'pending_approval' ? 'mfaPolicy.codePending' : 'mfaPolicy.codeNone')}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-2 text-xs text-muted-foreground">{t('mfaPolicy.howToReady')}</p>
                            </div>
                        )}
                        <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); if (reason.trim().length >= 10) change.mutate(!data.mandatory) }}>
                            <Label htmlFor="mfa-policy-reason">{t('mfaPolicy.reasonLabel')}</Label>
                            <div className="flex gap-2">
                                <Input id="mfa-policy-reason" value={reason} maxLength={255} onChange={(e) => setReason(e.target.value)} />
                                <Button type="submit" variant={data.mandatory ? 'outline' : 'default'} disabled={change.isPending || reason.trim().length < 10}>
                                    {data.mandatory ? t('mfaPolicy.switchOff') : t('mfaPolicy.switchOn')}
                                </Button>
                            </div>
                            {!data.mandatory && withoutCode > 0 && (
                                <p className="text-xs text-destructive" data-testid="mfa-policy-warning">{t('mfaPolicy.switchOnWarning', { count: withoutCode })}</p>
                            )}
                            {change.isError && <p className="text-sm text-destructive" role="alert">{handleApiError(change.error)}</p>}
                        </form>

                        {/* Stage 7: a fresh proof of a strong device before a high-risk action. */}
                        <div className="space-y-2 border-t pt-4" data-testid="step-up-policy" data-enforced={data.stepUpEnforced}>
                            <p className="text-sm font-medium">{t('highRisk.title')}</p>
                            <p className="text-sm text-muted-foreground">{t('highRisk.description')}</p>
                            <p className="text-sm">{data.stepUpEnforced ? t('highRisk.stateOn') : t('highRisk.stateOff')}</p>
                            <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); if (stepUpReason.trim().length >= 10) changeStepUp.mutate(!data.stepUpEnforced) }}>
                                <Label htmlFor="step-up-policy-reason">{t('mfaPolicy.reasonLabel')}</Label>
                                <div className="flex gap-2">
                                    <Input id="step-up-policy-reason" value={stepUpReason} maxLength={255} onChange={(e) => setStepUpReason(e.target.value)} />
                                    <Button type="submit" variant={data.stepUpEnforced ? 'outline' : 'default'} disabled={changeStepUp.isPending || stepUpReason.trim().length < 10}>
                                        {data.stepUpEnforced ? t('highRisk.switchOff') : t('highRisk.switchOn')}
                                    </Button>
                                </div>
                                {changeStepUp.isError && <p className="text-sm text-destructive" role="alert">{handleApiError(changeStepUp.error)}</p>}
                            </form>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    )
}
